import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import ConnectionDestinationTable from "../destinations/ConnectionDestinationTable";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";
import { filterClasses } from "../common/tailwindClassnames";
import { Clear } from "../common/ProjectIcons";

type FilterOption = { label: string; value: string };

type ConnectionDetailsDestinationProps = {
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    onSelectDestination: Function,
    onRemoveDestination: Function,
    onViewDetails: Function,
    organizations: Organization[],
    loadingOrganizations: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onClickAddDestination: Function,
    onAddExistingDestinations: MouseEventHandler,
    onAddNewDestination?: (dest: Destination) => void,
    updatingDestinations: boolean,
    addExistingDest: boolean,
    globalSearchStr: string,
    onChangeGlobalDestinationSearch: Function,
    typeFilterValue: FilterOption | null,
    onChangeTypeFilter: Function,
    ownerFilterValue: FilterOption | null,
    onChangeOwnerFilter: Function,
    searchTerm?: string
}

const ConnectionDetailsDestination = ({
    destinations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    onSelectDestination,
    onRemoveDestination,
    onViewDetails,
    organizations,
    loadingOrganizations,
    destinationTypes,
    loadingDestinationTypes,
    onClickAddDestination,
    onAddExistingDestinations,
    onAddNewDestination,
    updatingDestinations,
    addExistingDest,
    globalSearchStr,
    onChangeGlobalDestinationSearch,
    typeFilterValue,
    onChangeTypeFilter,
    ownerFilterValue,
    onChangeOwnerFilter,
    searchTerm = "",
}: ConnectionDetailsDestinationProps) => {
    const { t } = useTranslation();
    return (
        <div className="mt-8">
            <div className="flex flex-row justify-between items-center w-full">
                <label className="text-secondary-gray">{t("Data from the provider are sent to the following destinations.")}</label>
            </div>
            <div className="flex flex-col mt-8 mb-12">
                {addExistingDest && <>
                    {/* Horizontal filter bar */}
                    <div className="flex flex-row gap-3 mb-3 flex-wrap">
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-off-black">{t("Search")}</span>
                            <GlobalSearch onChangeGlobalSearch={onChangeGlobalDestinationSearch} savedString={globalSearchStr} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-off-black">{t("Technology")}</span>
                            <SelectInput
                                name="type__in"
                                options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                                value={typeFilterValue}
                                onChange={onChangeTypeFilter}
                                isMulti={false}
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={false}
                                isLoading={loadingDestinationTypes}
                                className="w-48"
                                variant="connectionFilter"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold text-off-black">{t("Workspace")}</span>
                            <SelectInput
                                name="owner__in"
                                options={organizations.map(org => ({ label: org.name, value: org.id }))}
                                value={ownerFilterValue}
                                onChange={onChangeOwnerFilter}
                                isMulti={false}
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={false}
                                isLoading={loadingOrganizations}
                                className="w-48"
                                variant="connectionFilter"
                            />
                        </div>
                    </div>
                    {/* Filter chips */}
                    {(globalSearchStr || typeFilterValue || ownerFilterValue) &&
                        <div className="flex flex-row flex-wrap gap-2 mb-3">
                            {globalSearchStr && <span className={filterClasses.tag}>
                                {globalSearchStr}
                                <span className="cursor-pointer text-dark-green" onClick={() => onChangeGlobalDestinationSearch('')}><Clear /></span>
                            </span>}
                            {typeFilterValue && <span className={filterClasses.tag}>
                                {typeFilterValue.label}
                                <span className="cursor-pointer text-dark-green" onClick={() => onChangeTypeFilter(null)}><Clear /></span>
                            </span>}
                            {ownerFilterValue && <span className={filterClasses.tag}>
                                {ownerFilterValue.label}
                                <span className="cursor-pointer text-dark-green" onClick={() => onChangeOwnerFilter(null)}><Clear /></span>
                            </span>}
                        </div>
                    }
                </>}
                <ConnectionDestinationTable
                    newConnection={false}
                    showNewDestination={false}
                    destinations={destinations}
                    existingDestPhase0={existingDestPhase0}
                    existingDestPhase1={existingDestPhase1}
                    existingDestPhase2={existingDestPhase2}
                    existingDestPhase3={existingDestPhase3}
                    addExistingDestinations={addExistingDest}
                    onAddExistingDestinations={onAddExistingDestinations}
                    updatingDestinations={updatingDestinations}
                    onClickAddDestination={onClickAddDestination}
                    onViewDetails={onViewDetails}
                    onRemoveDestination={onRemoveDestination}
                    onSelectDestination={onSelectDestination}
                    searchTerm={searchTerm}
                />
            </div>
        </div>
    )
};

export default ConnectionDetailsDestination;
